// src/scenes/ManageUsers.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    IconButton,
    Grid,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Delete as DeleteIcon, Visibility, VisibilityOff } from '@mui/icons-material';

const ManageUsers = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingUserApiid, setEditingUserApiid] = useState(null);
    const [editingData, setEditingData] = useState({});
    const [newUserData, setNewUserData] = useState({
        FirstName: '',
        LastName: '',
        email: '',
        password: '',
        RoleID: '',
        department_id: '',
        isActive: true
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [visiblePasswords, setVisiblePasswords] = useState({});

    // Fetch users, roles, and departments on mount
    useEffect(() => {
        fetchUsers();
        fetchRoles();
        fetchDepartments();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await fetch('https://azuresql.swancreate.com/api/swa/ccps_users');
            if (!response.ok) throw new Error('Failed to fetch users');
            const data = await response.json();
            setUsers(data);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch users.');
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await fetch('https://azuresql.swancreate.com/api/swa/ccps_roles');
            if (!response.ok) throw new Error('Failed to fetch roles');
            const data = await response.json();
            setRoles(data);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch roles.');
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await fetch('https://azuresql.swancreate.com/api/swa/ccps_departments');
            if (!response.ok) throw new Error('Failed to fetch departments');
            const data = await response.json();
            setDepartments(data);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch departments.');
        }
    };

    const handleEditClick = (user) => {
        setEditingUserApiid(user.apiid);
        setEditingData({ ...user });
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditingData((prev) => ({ ...prev, [name]: value }));
    };

    // Remove the apiid field from the payload so it isn't declared twice in the PUT call.
    const handleSaveEdit = async (apiid) => {
        try {
            const { apiid: _, ...payload } = editingData;
            const response = await fetch(`https://azuresql.swancreate.com/api/swa/ccps_users/${apiid}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error("Error updating user:", errorText);
                throw new Error('Failed to update user');
            }
            setUsers((prev) => prev.map((user) => user.apiid === apiid ? { ...payload, apiid } : user));
            setEditingUserApiid(null);
            setSuccess('User updated successfully.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error("PUT Error:", err);
            setError('Failed to update user.');
        }
    };

    const handleCancelEdit = () => {
        setEditingUserApiid(null);
        setEditingData({});
    };

    const handleNewUserChange = (e) => {
        const { name, value } = e.target;
        setNewUserData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddUser = async () => {
        try {
            const response = await fetch('https://azuresql.swancreate.com/api/swa/ccps_users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUserData)
            });
            if (!response.ok) throw new Error('Failed to add user');
            const addedUser = await response.json();
            setUsers((prev) => [...prev, addedUser]);
            setNewUserData({
                FirstName: '',
                LastName: '',
                email: '',
                password: '',
                RoleID: '',
                department_id: '',
                isActive: true
            });
            setSuccess('User added successfully.');
            setTimeout(() => setSuccess(''), 3000);
            setOpenAddDialog(false);
        } catch (err) {
            console.error(err);
            setError('Failed to add user.');
        }
    };

    const handleDeleteUser = async (apiid) => {
        try {
            const response = await fetch(`https://azuresql.swancreate.com/api/swa/ccps_users/${apiid}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error('Failed to delete user');
            setUsers((prev) => prev.filter((user) => user.apiid !== apiid));
            setSuccess('User deleted successfully.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error(err);
            setError('Failed to delete user.');
        }
    };

    const togglePasswordVisibility = (apiid) => {
        setVisiblePasswords((prev) => ({
            ...prev,
            [apiid]: !prev[apiid]
        }));
    };

    const filteredUsers = users.filter((user) => {
        const search = searchTerm.toLowerCase();
        return (
            (user.FirstName ? user.FirstName.toLowerCase() : '').includes(search) ||
            (user.LastName ? user.LastName.toLowerCase() : '').includes(search) ||
            (user.email ? user.email.toLowerCase() : '').includes(search)
        );
    });

    const handleOpenAddDialog = () => {
        setOpenAddDialog(true);
    };

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>
                Manage Users
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Search Field */}
            <TextField
                label="Search Users"
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
            />

            {/* Add User Button */}
            <Button variant="contained" onClick={handleOpenAddDialog} sx={{ mb: 2 }}>
                Add User
            </Button>

            {/* Add User Dialog with Two Column Form */}
            <Dialog open={openAddDialog} onClose={handleCloseAddDialog} fullWidth maxWidth="md">
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="First Name"
                                name="FirstName"
                                variant="outlined"
                                fullWidth
                                value={newUserData.FirstName}
                                onChange={handleNewUserChange}
                                InputLabelProps={{ style: { color: '#fff' } }}
                                sx={{
                                    backgroundColor: '#444',
                                    borderRadius: 1,
                                    '& .MuiInputBase-input': { color: '#fff' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Last Name"
                                name="LastName"
                                variant="outlined"
                                fullWidth
                                value={newUserData.LastName}
                                onChange={handleNewUserChange}
                                InputLabelProps={{ style: { color: '#fff' } }}
                                sx={{
                                    backgroundColor: '#444',
                                    borderRadius: 1,
                                    '& .MuiInputBase-input': { color: '#fff' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Email"
                                name="email"
                                variant="outlined"
                                fullWidth
                                value={newUserData.email}
                                onChange={handleNewUserChange}
                                InputLabelProps={{ style: { color: '#fff' } }}
                                sx={{
                                    backgroundColor: '#444',
                                    borderRadius: 1,
                                    '& .MuiInputBase-input': { color: '#fff' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Password"
                                name="password"
                                type="password"
                                variant="outlined"
                                fullWidth
                                value={newUserData.password}
                                onChange={handleNewUserChange}
                                InputLabelProps={{ style: { color: '#fff' } }}
                                sx={{
                                    backgroundColor: '#444',
                                    borderRadius: 1,
                                    '& .MuiInputBase-input': { color: '#fff' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth sx={{ backgroundColor: '#444', borderRadius: 1 }}>
                                <InputLabel sx={{ color: '#fff' }}>Role</InputLabel>
                                <Select
                                    label="Role"
                                    name="RoleID"
                                    value={newUserData.RoleID}
                                    onChange={handleNewUserChange}
                                    sx={{ color: '#fff' }}
                                >
                                    {roles.map((role) => (
                                        <MenuItem key={role.apiid} value={role.RoleID} sx={{ color: '#fff' }}>
                                            {role.RoleName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth sx={{ backgroundColor: '#444', borderRadius: 1 }}>
                                <InputLabel sx={{ color: '#fff' }}>Department</InputLabel>
                                <Select
                                    label="Department"
                                    name="department_id"
                                    value={newUserData.department_id}
                                    onChange={handleNewUserChange}
                                    sx={{ color: '#fff' }}
                                >
                                    {departments.map((dept) => (
                                        <MenuItem key={dept.apiid} value={dept.department_id} sx={{ color: '#fff' }}>
                                            {dept.DepartmentName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddUser}>Add User</Button>
                </DialogActions>
            </Dialog>

            {/* Users Table with Navy Blue Background */}
            <TableContainer component={Paper} sx={{ backgroundColor: '#001f3f' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: '#fff' }}>First Name</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Last Name</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Email</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Password</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Role</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Department</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredUsers.map((user, index) => (
                            <TableRow key={user.apiid} sx={{ backgroundColor: index % 2 ? '#002752' : '#001f3f' }}>
                                <TableCell sx={{ color: '#fff' }}>
                                    {editingUserApiid === user.apiid ? (
                                        <TextField
                                            name="FirstName"
                                            value={editingData.FirstName || ''}
                                            onChange={handleEditChange}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                backgroundColor: '#444',
                                                '& .MuiInputBase-input': { color: '#fff' }
                                            }}
                                            InputLabelProps={{ style: { color: '#fff' } }}
                                        />
                                    ) : (
                                        user.FirstName
                                    )}
                                </TableCell>
                                <TableCell sx={{ color: '#fff' }}>
                                    {editingUserApiid === user.apiid ? (
                                        <TextField
                                            name="LastName"
                                            value={editingData.LastName || ''}
                                            onChange={handleEditChange}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                backgroundColor: '#444',
                                                '& .MuiInputBase-input': { color: '#fff' }
                                            }}
                                            InputLabelProps={{ style: { color: '#fff' } }}
                                        />
                                    ) : (
                                        user.LastName
                                    )}
                                </TableCell>
                                <TableCell sx={{ color: '#fff' }}>
                                    {editingUserApiid === user.apiid ? (
                                        <TextField
                                            name="email"
                                            value={editingData.email || ''}
                                            onChange={handleEditChange}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                backgroundColor: '#444',
                                                '& .MuiInputBase-input': { color: '#fff' }
                                            }}
                                            InputLabelProps={{ style: { color: '#fff' } }}
                                        />
                                    ) : (
                                        user.email
                                    )}
                                </TableCell>
                                <TableCell sx={{ color: '#fff' }}>
                                    {editingUserApiid === user.apiid ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <TextField
                                                name="password"
                                                value={editingData.password || ''}
                                                onChange={handleEditChange}
                                                variant="outlined"
                                                size="small"
                                                type={visiblePasswords[user.apiid] ? 'text' : 'password'}
                                                sx={{
                                                    backgroundColor: '#444',
                                                    '& .MuiInputBase-input': { color: '#fff' }
                                                }}
                                                InputLabelProps={{ style: { color: '#fff' } }}
                                            />
                                            <IconButton onClick={() => togglePasswordVisibility(user.apiid)} sx={{ color: '#fff' }}>
                                                {visiblePasswords[user.apiid] ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography>
                                                {visiblePasswords[user.apiid] ? user.password : '������'}
                                            </Typography>
                                            <IconButton onClick={() => togglePasswordVisibility(user.apiid)} sx={{ color: '#fff' }}>
                                                {visiblePasswords[user.apiid] ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell sx={{ color: '#fff' }}>
                                    {editingUserApiid === user.apiid ? (
                                        <FormControl fullWidth size="small" sx={{ backgroundColor: '#444', borderRadius: 1 }}>
                                            <InputLabel sx={{ color: '#fff' }}>Role</InputLabel>
                                            <Select
                                                name="RoleID"
                                                value={editingData.RoleID || ''}
                                                onChange={handleEditChange}
                                                label="Role"
                                                sx={{ color: '#fff' }}
                                            >
                                                {roles.map((role) => (
                                                    <MenuItem key={role.apiid} value={role.RoleID} sx={{ color: '#fff' }}>
                                                        {role.RoleName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        roles.find((r) => Number(r.RoleID) === Number(user.RoleID))?.RoleName || ''
                                    )}
                                </TableCell>
                                <TableCell sx={{ color: '#fff' }}>
                                    {editingUserApiid === user.apiid ? (
                                        <FormControl fullWidth size="small" sx={{ backgroundColor: '#444', borderRadius: 1 }}>
                                            <InputLabel sx={{ color: '#fff' }}>Department</InputLabel>
                                            <Select
                                                name="department_id"
                                                value={editingData.department_id || ''}
                                                onChange={handleEditChange}
                                                label="Department"
                                                sx={{ color: '#fff' }}
                                            >
                                                {departments.map((dept) => (
                                                    <MenuItem key={dept.apiid} value={dept.department_id} sx={{ color: '#fff' }}>
                                                        {dept.DepartmentName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        departments.find((d) => Number(d.department_id) === Number(user.department_id))?.DepartmentName || ''
                                    )}
                                </TableCell>
                                <TableCell sx={{ color: '#fff' }}>
                                    {editingUserApiid === user.apiid ? (
                                        <>
                                            <IconButton onClick={() => handleSaveEdit(user.apiid)}>
                                                <SaveIcon />
                                            </IconButton>
                                            <IconButton onClick={handleCancelEdit}>
                                                <CancelIcon />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <IconButton onClick={() => handleEditClick(user)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleDeleteUser(user.apiid)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ManageUsers;
